import React, { useState, useRef, useEffect, useMemo } from 'react';
import { hoverHelpers } from 'shared-helpers';

import {
    getDate,
    getDisplayAmount,
    getStage,
} from '/src/components/SingleSICE/Reusable/FundingRounds/Helpers/fundingViewHelpers';
import {
    buildStructuredInvestmentRows,
    getAmountValue,
} from '/src/components/ICESections/Investments/helpers';
import { renderProxyAmount } from '/src/pages/directories/helpers/fundingRowHelpers';
import LinkedEntity from '/src/components/ICESections/Investments/Components/LinkedEntity';
import OtherInvestorsModal from './Components/OtherInvestorsModal';
import ValuationValue from '/src/components/Reusable/ValuationValue';
import { MOB_BADGE_COLORS, getInitials } from '/src/components/ICESections/Shared/mobileListUtils';

// Maps table column keys to API sort field names
const COLUMN_SORT_KEYS = {
    company: 'investedin_data_name_sort',
    hq: 'investedin_hq_data_name_sort',
    stage: 'funding_stage_data_name_sort',
    date: 'date_raised',
    investment: 'invested_amount_sort',
    roundSize: 'round_size_sort',
    valuation: 'valuation_sort',
    investors: 'investors_count_sort',
    followOn: 'follow_on_count',
    verified: 'verification_status',
};

const InvestmentTable = ({ rounds, entityId, entityType, hasDirectoryAccess = false, onSort, sortConfig }) => {
    const [activeRow, setActiveRow] = useState(null);
    const [scrollPos, setScrollPos] = useState(0);
    const [maxScroll, setMaxScroll] = useState(null);
    const [expandedMobileRows, setExpandedMobileRows] = useState([]);
    const toggleMobileRow = (key) => setExpandedMobileRows(prev =>
        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
    const wrapperRef = useRef(null);

    const rows = useMemo(() => buildStructuredInvestmentRows({
        rounds,
        entityId,
        entityType,
    }), [rounds, entityId, entityType]);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const update = () => {
            setScrollPos(el.scrollLeft);
            setMaxScroll(el.scrollWidth - el.clientWidth);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [rows]);

    const handleScroll = () => {
        const el = wrapperRef.current;
        if (!el) return;
        setScrollPos(el.scrollLeft);
        setMaxScroll(el.scrollWidth - el.clientWidth);
    };

    const scrollTable = (direction) => {
        const el = wrapperRef.current;
        if (!el) return;
        const scrollVal = Math.max(el.clientWidth - 149, 150);
        el.scrollLeft += direction === 'left' ? -scrollVal : scrollVal;
    };

    if (!rows.length) {
        return null;
    }

    /**
     * Returns a sort indicator icon for the given column key.
     * Shows nothing when onSort is not wired up (non-investor-investment tables).
     */
    const getSortIcon = (colKey) => {
        if (!onSort) return null;
        const apiKey = COLUMN_SORT_KEYS[colKey];
        if (!sortConfig || sortConfig.key !== apiKey) {
            return <i className="fas fa-sort structured-investments-table__sort-icon" />;
        }
        if (sortConfig.dir === 'asc') {
            return <i className="fas fa-sort-up structured-investments-table__sort-icon structured-investments-table__sort-icon--active" />;
        }
        return <i className="fas fa-sort-down structured-investments-table__sort-icon structured-investments-table__sort-icon--active" />;
    };

    const makeSortProps = (colKey) => {
        if (!onSort) return {};
        const isSorted = sortConfig && sortConfig.key === COLUMN_SORT_KEYS[colKey] && sortConfig.dir;
        return {
            onClick: () => onSort(COLUMN_SORT_KEYS[colKey]),
            className: `structured-investments-table__th--sortable${isSorted ? ' structured-investments-table__th--sorted' : ''}`,
        };
    };

    return (
        <>
            <div className="structured-investments-table-shell">
                <div className="structured-investments-table-scroll-area">
                    {scrollPos > 0 && (
                        <button className="structured-investments-scroll-btn structured-investments-scroll-btn--left" onClick={() => scrollTable('left')}>
                            <i className="fas fa-chevron-left" />
                        </button>
                    )}
                    {maxScroll > 0 && scrollPos < maxScroll && (
                        <button className="structured-investments-scroll-btn structured-investments-scroll-btn--right" onClick={() => scrollTable('right')}>
                            <i className="fas fa-chevron-right" />
                        </button>
                    )}
                    <div
                        className="structured-investments-table-wrapper"
                        ref={wrapperRef}
                        onScroll={handleScroll}
                    >
                        <table className="structured-investments-table">
                            <thead>
                                <tr className="structured-investments-table__header">
                                    <th {...makeSortProps('company')}>Company {getSortIcon('company')}</th>
                                    <th {...makeSortProps('hq')}>HQ {getSortIcon('hq')}</th>
                                    <th {...makeSortProps('stage')}>Stage {getSortIcon('stage')}</th>
                                    <th {...makeSortProps('date')}>Date {getSortIcon('date')}</th>
                                    <th {...makeSortProps('investment')}>Investment {getSortIcon('investment')}</th>
                                    <th {...makeSortProps('roundSize')}>Round Size {getSortIcon('roundSize')}</th>
                                    <th {...makeSortProps('valuation')}>Valuation {getSortIcon('valuation')}</th>
                                    <th {...makeSortProps('investors')}>Investors {getSortIcon('investors')}</th>
                                    <th {...makeSortProps('followOn')}>Follow-on {getSortIcon('followOn')}</th>
                                    <th {...makeSortProps('verified')}><i className="fas fa-check structured-investments-table__verified-header-icon" />{getSortIcon('verified')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows?.map((row) => {
                                    const otherInvestorsCount = row?.otherInvestors?.length;

                                    return (
                                        <tr key={row.key}>
                                            <td data-label="Company">
                                                <LinkedEntity
                                                    entity={row.startup}
                                                    displayValue={row.startup?.name}
                                                    onMouseEnter={(event) => hoverHelpers.handleEntityHover(row.startup, event, hasDirectoryAccess)}
                                                    onMouseLeave={() => hoverHelpers.handleMouseOut()}
                                                />
                                            </td>
                                            <td data-label="HQ">{row.startupHq}</td>
                                            <td data-label="Stage">{getStage(row.stage)}</td>
                                            <td data-label="Date">{getDate(row.dateAnnounced)}</td>
                                            <td
                                                data-label="Investment"
                                                className="structured-investments-table__amount-cell"
                                            >
                                                {getDisplayAmount(
                                                    row.focusedInvestor,
                                                    row.estimatedAmount,
                                                    row.verificationStatus,
                                                    false
                                                )}
                                            </td>
                                            <td data-label="Round Size" className="structured-investments-table__amount-cell">
                                                {row.amount
                                                    ? getAmountValue(row.amount)
                                                    : renderProxyAmount(row.undisclosedAmount) || 'Undisclosed'
                                                }
                                            </td>
                                            <td
                                                data-label="Valuation"
                                                className="structured-investments-table__valuation-cell"
                                            >
                                                <ValuationValue valuation={row.valuation} />
                                            </td>
                                            <td data-label="Investors">
                                                {otherInvestorsCount > 0 ? (
                                                    <button
                                                        type="button"
                                                        className="structured-investments-table__lps-btn"
                                                        onClick={() => setActiveRow(row)}
                                                        title={`${otherInvestorsCount} Investors`}
                                                        aria-label={`View ${otherInvestorsCount} investors for ${row.startup?.name || 'this investment'}`}
                                                    >
                                                        {otherInvestorsCount} {otherInvestorsCount === 1 ? 'Investor' : 'Investors'}
                                                    </button>
                                                ) : (
                                                    <span className="structured-investments-table__empty">
                                                    -
                                                    </span>
                                                )}
                                            </td>
                                            <td data-label="Follow-on">{row.followOnDisplay}</td>
                                            <td data-label="Verified">
                                                {row.verificationStatus === 1 ? (
                                                    <i className="fas fa-check structured-investments-table__verified-check" title="Verified" />
                                                ) : (
                                                    <i className="far fa-circle structured-investments-table__verified-pending" title="Pending verification" />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="structured-investments-mobile-list">
                    {rows.map((row, idx) => {
                        const isOpen = expandedMobileRows.includes(row.key);
                        const otherInvestorsCount = row?.otherInvestors?.length;
                        const col = MOB_BADGE_COLORS[idx % MOB_BADGE_COLORS.length];

                        return (
                            <div
                                key={row.key}
                                className="mob-investment-row"
                                onClick={() => toggleMobileRow(row.key)}
                            >
                                <div className="mob-investment-row__top">
                                    <div className="mob-badge" style={{ background: col.bg, color: col.txt }}>
                                        {getInitials(row.startup?.name)}
                                    </div>
                                    <div className="mob-investment-row__main">
                                        <div onClick={(e) => e.stopPropagation()}>
                                            <LinkedEntity entity={row.startup} displayValue={row.startup?.name} />
                                        </div>
                                        <div className="mob-investment-row__meta">
                                            {row.startupHq} · {getStage(row.stage)}
                                        </div>
                                    </div>
                                    <div className="mob-investment-row__right">
                                        <span className="mob-investment-row__amount">
                                            {getDisplayAmount(row.focusedInvestor, row.estimatedAmount, row.verificationStatus, false)}
                                        </span>
                                        <div className="mob-investment-row__status-row">
                                            {row.verificationStatus === 1 ? (
                                                <i className="fas fa-check structured-investments-table__verified-check" />
                                            ) : (
                                                <i className="far fa-circle structured-investments-table__verified-pending" />
                                            )}
                                            <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} mob-investment-row__chevron`} />
                                        </div>
                                    </div>
                                </div>

                                {isOpen && (
                                    <div className="mob-investment-row__expand">
                                        <div className="mob-expand-grid">
                                            <div className="mob-expand-cell">
                                                <div className="mob-expand-cell__lbl">Stage</div>
                                                <div className="mob-expand-cell__val">{getStage(row.stage)}</div>
                                            </div>
                                            <div className="mob-expand-cell">
                                                <div className="mob-expand-cell__lbl">Date</div>
                                                <div className="mob-expand-cell__val">{getDate(row.dateAnnounced)}</div>
                                            </div>
                                            <div className="mob-expand-cell">
                                                <div className="mob-expand-cell__lbl">Round Size</div>
                                                <div className="mob-expand-cell__val">
                                                    {row.amount
                                                        ? getAmountValue(row.amount)
                                                        : renderProxyAmount(row.undisclosedAmount) || 'Undisclosed'
                                                    }
                                                </div>
                                            </div>
                                            <div className="mob-expand-cell">
                                                <div className="mob-expand-cell__lbl">Valuation</div>
                                                <div className="mob-expand-cell__val">
                                                    <ValuationValue valuation={row.valuation} />
                                                </div>
                                            </div>
                                            <div className="mob-expand-cell">
                                                <div className="mob-expand-cell__lbl">Investors</div>
                                                <div className="mob-expand-cell__val">
                                                    {otherInvestorsCount > 0 ? (
                                                        <button
                                                            type="button"
                                                            className="structured-investments-table__lps-btn"
                                                            onClick={(e) => { e.stopPropagation(); setActiveRow(row); }}
                                                        >
                                                            {otherInvestorsCount}
                                                        </button>
                                                    ) : (
                                                        <span className="structured-investments-table__empty">-</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="mob-expand-cell">
                                                <div className="mob-expand-cell__lbl">Follow-on</div>
                                                <div className="mob-expand-cell__val">{row.followOnDisplay}</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <OtherInvestorsModal
                activeRow={activeRow}
                hasDirectoryAccess={hasDirectoryAccess}
                onClose={() => setActiveRow(null)}
            />
        </>
    );
};

export default InvestmentTable;
